import { PartnerSlug, ProductName } from "types/general";
import { VerificationStatus } from "types/verification";
import { OnboardingState } from "types/onboarding";
import { ApiIncomeTaxReturn } from "../incomeTaxReturns";
import { ApiNotification } from "../notifications";
import { ApiAdminRepaymentInstance } from "./repaymentInstance";

export interface ApiAdminUserEligibilityResult {
  eligible: boolean;
  applicationStatus: string;
  version: number;
  userActionableCriteria: Record<string, boolean>;
  completedAt: string | null;
  eligibleAt: string | null;
  ineligibleAt: string | null;
  notStartedAt: string | null;
  pendingAt: string | null;
  rejectedAt: string | null;
  requiresActionAt: string | null;
  results: Record<string, any>;
}

export interface SafeToSpendComponents {
  safeToSpend: number;
  maxSafeToSpend: number;
  nextPayday: string;
  availableBankBalance: number;
  accruedNetEarnings: number;
  upcomingExpensesAmount: number;
  totalExpensesAmount: number;
  outstandingResetTransactions: number;
  incomeTaxRefundAmount: number;
}

export interface ApiAdminUserPayDates {
  paveDerived?: string;
  pinwheelDerived?: string;
  resetPaystubDerived?: string;
  payrollAccountPayPeriodStartDateDerived?: string;
  systemChosenDebitDate?: string;
}

export interface ApiAdminUserAcquisitionMetadata {
  referralSlug?: string;
  productEntryPoint?: string;
  utmCampaign?: string;
  utmSource?: string;
  utmMedium?: string;
  groupId?: string;
  payrollLinkingSkipMethod?: "manual" | "automatic";
}

export interface ApiAdminUserMini {
  id: string;
  createdAt: string;
  lastSignInAt: string;
  inProgressOnboardingState: OnboardingState;
  mostRecentInProgressProduct: ProductName;
  deactivated: boolean;
  name: string;
  email: string;
  admin: boolean;
  dsl?: number;
  latestTransactionDate?: string;
  payDate?: string;
  payDates?: ApiAdminUserPayDates;
  acquisitionMetadata?: ApiAdminUserAcquisitionMetadata;
  userConfig: ApiAdminUserConfig;
}

export interface ApiAdminUsers {
  users: ApiAdminUserMini[];
  totalPages: number;
}

export interface ApiAdminUsersRequest {
  page: number;
  sortBy?: string;
}

export interface ApiAdminUserExpense {
  id: string;
  averageAmountInt: number;
  merchantName?: string;
  description: string;
  lastOccurrenceDate: string;
}

export interface ApiAdminUserPinwheelPaystub {
  payDate: string;
  payPeriodStartDate: string;
  payPeriodEndDate: string;
  employerName: string;
  grossPayAmountInt: number;
  netPayAmountInt: number;
  checkAmountInt: number;
}

interface Card {
  id: string;
}

export enum ApiPayFrequency {
  weekly = "weekly",
  biweekly = "bi_weekly",
  semimonthly = "semi_monthly",
  monthly = "monthly",
}

export interface ApiAdminUserEwaMultiplierByRepaymentCount {
  payrollLinked: number[];
  payrollUnlinked: number[];
}

export interface ApiAdminUserConfig {
  partnerSlug?: string;
  referralSlug?: string;
  customRepaymentPlan: boolean;
  enableProbationaryLimit: boolean;
  enableFutureDatedDirectDepositLimit: boolean;
  alwaysIssuePhysicalCard: boolean;
  requireInternalReviewAfterIdVerification: boolean;
  repaymentMethod: string;
  ewaMultiplierByRepaymentCount: ApiAdminUserEwaMultiplierByRepaymentCount;
  incomeTaxRefundAdvanceMultiplier: number;
  inactive: boolean;
  requirePayrollLinking: boolean;
}

export interface ApiAdminUserRepaymentInfo {
  lastRepayment?: {
    amount: number;
    networkStateInitiatedAt: string;
    networkStateSettledAt: string;
    networkStateReversedAt: string;
    createdAt: string;
  };
  lastRepaymentAt: string;
  numRepayments: number;
  lastReversedAchDebitRepayment?: {
    amount: number;
    networkStateInitiatedAt: string;
    networkStateReversedAt: string;
    createdAt: string;
  };
  lastReversedAchDebitAt: string;
  numReversedAchDebitRepayments: number;
  lastNotReversedAchDebitRepayment?: {
    amount: number;
    networkStateInitiatedAt: string;
    networkStateSettledAt: string;
    createdAt: string;
  };
  lastNotReversedAchDebitAt: string;
  numNotReversedAchDebitRepayments: number;
}

export interface ApiAdminUser extends ApiAdminUserMini {
  linkedBankAccountName: string;
  payrollAccount: {
    institutionName: string;
    compensationUnit: string;
    compensationAmountInt: number;
    payFrequency: ApiPayFrequency;
    payPeriodStartDate: string | null;
    overriddenAt: string | null;
  };
  payDates: ApiAdminUserPayDates;
  userConfig: ApiAdminUserConfig;
  annualizedCompensation: number;
  safeToSpendComponents: SafeToSpendComponents;
  recurringExpenses: ApiAdminUserExpense[];
  lastPinwheelPaystub?: ApiAdminUserPinwheelPaystub;
  identityVerificationStatus: VerificationStatus;
  cards?: {
    physical?: Card;
    virtual?: Card;
  };
  eligible: boolean | null;
  eligibilityResult?: ApiAdminUserEligibilityResult;
  hubspotContactUrl: string;
  syncteraCustomerUrl?: string;
  payrollLinkingStatus: string;
  incomeTaxReturns: ApiIncomeTaxReturn[];
  onboardingStates: {
    [key in keyof typeof ProductName]: OnboardingState;
  };
  notifications: ApiNotification[];
  repaymentInfo: ApiAdminUserRepaymentInfo;
  potentialDuplicateUsers: ApiAdminUserPotentialDuplicateUser[];
  scheduledRepaymentInstances: ApiAdminRepaymentInstance[];
  lastProcessedRepaymentInstance?: ApiAdminRepaymentInstance;
  lastCancelledRepaymentInstance?: ApiAdminRepaymentInstance;
}

export interface ApiAdminUserPinwheelStatus {
  connected: boolean;
  monitoringStatus: string;
  pinwheelAccountId: string;
  linkToken: string;
  dataRefreshedAt: {
    directDepositAllocations: string;
    income: string;
    employment: string;
    identity: string;
    shifts: string;
    paystubs: string;
  };
}

export interface ApiAdminUserDirectDepositAllocation {
  accountName: string;
  allocationType: string;
  amount: number;
  percentage: number;
  priority: number;
  routingNumber: number;
  maskedAccountNumber: number;
}

export interface ApiAdminUserDirectDeposit {
  allocations: ApiAdminUserDirectDepositAllocation[];
}

export interface ApiAdminUserPlaidTransaction {
  id: string;
  date: string;
  amount: number;
  category: string;
  categoryId: string;
  merchantName: string;
  name: string;
  transactionType: string;
}

export interface ApiAdminUserPlaidStatus {
  balancesUpdatedAt: string;
  error: string;
  transactionsFailUpdatedAt: string;
  transactionsSuccessfulUpdatedAt: string;
  plaidItemId: string;
}

export interface ApiAdminUserPlaidTransactions {
  plaidTransactions: ApiAdminUserPlaidTransaction[];
}

export interface ApiAdminUserAnnualizedIncome {
  sortedNetPayAmounts: number[];
  sortedNetPayRatios: number[];
  annualizedPayWithMin: number;
  annualizedPayWithMax: number;
  annualizedPayWithAvg: number;
}

export interface ApiAdminUserPaveStatus {
  characteristics: {};
  incomes: {};
  scores: {};
}

export interface ApiAdminUserPotentialDuplicateUser {
  url: string;
  shortId: string;
  matchScore: number;
}

export interface ApiAdminFunnelUser extends ApiAdminUserAcquisitionMetadata {
  partnerSlug?: PartnerSlug; // This is derived from userConfig.partnerSlug acquisitionMetadata.referralSlug
  onboardingState: OnboardingState;
  eligible: boolean;
}
